import React, { forwardRef, useEffect, useMemo } from 'react';
import { useRelativeDate } from '~/react-hooks/use-date';
import { TestimonialRespondentsMedia as RespondentMedia } from '~/types/models/testimonial';
import ResourceTagBar from '../../Tags/Components/ResourceTagBar';
import { TaggableResourceType } from '~/types/models/tags';
import AssetDetails from './AssetDetails';
import AssetContact from './AssetContact';
import ResponseActionLinks from './ResponseActionLinks';
import { useTaggableResourcePage } from '../hooks/use-taggable-resource-page';
import { useThumbnailWithFallback } from '../hooks/use-thumbnail-with-fallback';
import {
  EmbedTile,
  EmbedTileProps,
  EmbedTileType,
  MediaType,
} from '@vimily/bonjoro-ui-components-v2/EmbedTile';
import LockIcon, { LockIconVariant } from '../../Common/Components/LockIcon';
import { NewTag } from './NewTag';
import { parseRichTextToString, getRichTextFromSlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import cx from 'classnames';
import { startUpgradeModalFlow } from '~/App/Components/Modals/UpgradeModalFlow';
import { UpgradeModalTrigger } from '~/App/Components/Modals/UpgradeModalFlow/util';

export interface RespondentsMediaListItem {
  item: RespondentMedia;
  redirectUrl?: string;
  onActionSuccess: () => void;
  onThumbnailLoad?: () => void;
  listItemClassName?: string;
  hideTagBar?: boolean;
}

const RespondentsMediaItem = forwardRef<
  HTMLDivElement,
  RespondentsMediaListItem
>((props, detailsContainerRef) => {
  const {
    item,
    redirectUrl,
    onActionSuccess,
    onThumbnailLoad,
    hideTagBar = false,
  } = props;

  const taggableResourcePage = useTaggableResourcePage();
  const { thumbnail, onThumbnailLoadError } = useThumbnailWithFallback({
    imageUrl: item?.thumb_url,
  });
  /*
   * A respondent media can be created but not submitted yet.
   * It is not considered created for the user until the respondent have submitted it.
   * once submitted, the media could not be updated further unless deleted.
   */
  const createdAgo = useRelativeDate(item.created_at);
  const locked = !!item.is_locked;

  const promptTestimonialUpgradeModal = () => {
    if (locked) {
      startUpgradeModalFlow({
        trigger: UpgradeModalTrigger.GenericTestimonialsUpgrade,
      });
      return true;
    }

    return false;
  };

  const embedTileProps: EmbedTileProps = useMemo(() => {
    const props = {
      type: EmbedTileType.BonjoroResponse,
      className: cx('!min-h-[195px]', {
        grayscale: locked,
      }),
      playable: !locked,
      hideDateTime: true,
      hideReaction: true,
      onClick: locked ? promptTestimonialUpgradeModal : undefined,
    };

    if (item?.respondent?.name) {
      props['profile'] = {
        name: item?.respondent?.name ?? undefined,
        imageUrl: item?.respondent?.image ?? undefined,
        tag: item?.respondent?.position ?? undefined,
      };
    }

    if (item?.video_url) {
      props['media'] = [
        {
          type: MediaType.Video,
          url: item?.video_url,
          transcription: item.transcript?.captions?.sentences,
          transcriptionClassName: '!text-xs',
          onClick: locked ? promptTestimonialUpgradeModal : undefined,
          hidePlayIcon: locked,
          thumbnailUrl: thumbnail,
          onThumbnailLoad: onThumbnailLoad,
          onThumbnailLoadError,
        },
      ];
    }

    if (item?.text_response_rich != null) {
      const richText = getRichTextFromSlateData(item?.text_response_rich);

      if (parseRichTextToString(richText) != '') {
        props['text'] = richText;
      }
    }

    return props;
  }, [item, thumbnail]);

  useEffect(() => {
    if (
      !item?.video_url &&
      item?.text_response_rich &&
      item?.text_response_rich.length > 0
    ) {
      onThumbnailLoad?.();
    }
  }, []);

  return (
    <>
      <div className='block w-full h-fit relative flex flex-col bg-white shadow rounded overflow-auto'>
        {!locked && !item.viewed && <NewTag />}
        <EmbedTile {...embedTileProps} />
        {locked && (
          <LockIcon
            className='absolute inset-0 m-auto'
            variant={LockIconVariant.Black}
          />
        )}
      </div>
      <div ref={detailsContainerRef}>
        <AssetDetails
          name={item.testimonial.name}
          redirectUrl={redirectUrl}
          actionLinksComponent={
            <ResponseActionLinks
              response={item}
              onActionSuccess={onActionSuccess}
            />
          }
        >
          <AssetContact name={item.respondent.name ?? ''} />
          {createdAgo}
        </AssetDetails>
        {!hideTagBar && (
          <ResourceTagBar
            emptyText='Add a tag'
            resource={item}
            onTagResourceSuccess={onActionSuccess}
            onUnTagResourceSuccess={onActionSuccess}
            resourceType={TaggableResourceType.TestimonialResponse}
            sourcePage={taggableResourcePage}
          />
        )}
      </div>
    </>
  );
});

export default RespondentsMediaItem;
